import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import CreditActions, { CreditSelectors } from "Redux/CreditRedux";

export default function CreditBalanceDisplay() {
  const dispatch = useDispatch();
  const credits = useSelector(CreditSelectors.getCredits);
  const isLowCredits = useSelector(CreditSelectors.isLowCredits);
  const isFetching = useSelector(CreditSelectors.isFetching);

  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch credits on mount
  useEffect(() => {
    dispatch(CreditActions.creditsFetchRequest());
  }, [dispatch]);

  // Close dropdown when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setShowDropdown(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const availableCredits = credits?.available || 0;
  const monthlyCredits = credits?.monthly_credits || 0;
  const topupCredits = credits?.topup_credits || 0;
  const reservedCredits = credits?.reserved || 0;
  const totalCredits = monthlyCredits + topupCredits;
  const usagePercent = totalCredits > 0 ? Math.round((availableCredits / totalCredits) * 100) : 0;

  const getProgressColor = () => {
    if (usagePercent > 50) return "bg-green-500";
    if (usagePercent > 20) return "bg-amber-500";
    return "bg-red-500";
  };

  const getBadgeStyle = () => {
    if (usagePercent <= 20) return "bg-red-100 text-red-700 hover:bg-red-200";
    if (isLowCredits) return "bg-amber-100 text-amber-700 hover:bg-amber-200";
    return "bg-gray-100 text-gray-700 hover:bg-gray-200";
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        aria-label={`${availableCredits} credits remaining. Click for details`}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${getBadgeStyle()}`}
        title="Click to see credit details"
      >
        <span className="text-base" aria-hidden="true">💳</span>
        {isFetching ? (
          <span className="animate-pulse">...</span>
        ) : (
          <span>{availableCredits.toLocaleString()} credits</span>
        )}
        <svg
          className={`w-4 h-4 transition-transform ${showDropdown ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-72 max-w-72 bg-white rounded-xl shadow-lg border border-gray-100 py-3 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
          {/* Credit progress bar */}
          <div className="px-4 pb-3 border-b border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Credit Balance
              </h4>
              <span className="text-xs font-medium text-gray-500">{usagePercent}% remaining</span>
            </div>
            <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden mb-3">
              <div
                className={`h-full rounded-full transition-all duration-500 ${getProgressColor()}`}
                style={{ width: `${Math.min(usagePercent, 100)}%` }}
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Monthly</span>
                <span className="font-medium text-gray-900">{monthlyCredits.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Top-up</span>
                <span className="font-medium text-gray-900">{topupCredits.toLocaleString()}</span>
              </div>
              {reservedCredits > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Reserved</span>
                  <span className="font-medium text-amber-600">-{reservedCredits.toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between text-sm pt-1.5 border-t border-gray-100">
                <span className="font-medium text-gray-700">Available</span>
                <span className="font-bold text-gray-900">{availableCredits.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Credit explainer tooltip */}
          <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50">
            <p className="text-xs text-gray-500">
              1 credit = 1 image, 1 animation, or 1 audio generation. A typical video uses 5-10 credits.
            </p>
          </div>

          {/* Low credits warning + buy more */}
          {usagePercent <= 20 && (
            <div className="px-4 py-2.5 bg-amber-50 border-b border-gray-100">
              <p className="text-xs text-amber-700 mb-1.5">Running low on credits</p>
              <Link
                to="/plans"
                onClick={() => setShowDropdown(false)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:text-primary-700 transition-colors"
              >
                Buy more credits
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </Link>
            </div>
          )}

          {/* Actions */}
          <div className="px-4 pt-2.5">
            <Link
              to="/credits/history"
              onClick={() => setShowDropdown(false)}
              className="block w-full px-3 py-2 text-center text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors"
            >
              View History
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
